/** Raw tabular data provided by a registrant (headers + rows, no sheet name). */
export interface DownloadData {
  headers: string[]
  rows: string[][]
}

/** A named, ready-to-export sheet produced by the download aggregator. */
export interface DownloadSheet {
  name: string
  rows: string[][]
}

export interface DownloadDataRegistration {
  key: string
  sheetName: string
  shouldFlatten?: boolean
  getData: () => DownloadData | null
  combineWithOthers?: boolean
}

type Listener = () => void

const shouldFlattenListeners = new Set<Listener>()
let hasAnyShouldFlatten = false

const registrations = new Map<string, Map<string, DownloadDataRegistration>>()

const FLAT_SUFFIX = " - Flat"
const FLAT_BASE_NAME_MAX = 31 - FLAT_SUFFIX.length

function buildSheetName(newSheetName: string, registration: DownloadDataRegistration): string {
  const baseName = newSheetName || "Data"

  if (!registration.shouldFlatten) return baseName
  if (baseName.endsWith(FLAT_SUFFIX)) return baseName

  const base = (baseName).slice(0, FLAT_BASE_NAME_MAX)
  return `${base}${FLAT_SUFFIX}`
}

export const downloadRegistry = {
  register(panelId: string, registration: DownloadDataRegistration) {
    let panelMap = registrations.get(panelId)
    if (!panelMap) {
      panelMap = new Map()
      registrations.set(panelId, panelMap)
    }

    panelMap.set(registration.key, {
      ...registration,
      sheetName: buildSheetName(registration.sheetName, registration),
    })

    emitShouldFlattenIfChanged()
  },

  unregister(panelId: string, key: string) {
    const panelMap = registrations.get(panelId)
    if (panelMap) {
      panelMap.delete(key)
      if (panelMap.size === 0) {
        registrations.delete(panelId)
      }
    }
    emitShouldFlattenIfChanged()
  },

  subscribeShouldFlatten(listener: Listener) {
    shouldFlattenListeners.add(listener)
    return () => shouldFlattenListeners.delete(listener)
  },

  getHasAnyShouldFlatten() {
    return hasAnyShouldFlatten
  },

  getRegistrations() {
    return new Map(
      Array.from(registrations, ([panelId, panelMap]) => [panelId, new Map(panelMap)]),
    )
  },

  /**
   * Updates the sheetName of all registrations for a panel.
   * Called when the user renames a tab
   */
  updateSheetNamesForPanel(panelId: string, newSheetName: string) {
    const panelMap = registrations.get(panelId)
    if (!panelMap) return
    for (const [key, registration] of panelMap) {
      panelMap.set(key, {
        ...registration,
        sheetName: buildSheetName(newSheetName, registration),
      })
    }
  },

  clear() {
    registrations.clear()
    emitShouldFlattenIfChanged()
  },
}

function computeHasAnyShouldFlatten(): boolean {
  for (const panelMap of registrations.values()) {
    for (const registration of panelMap.values()) {
      if (registration?.shouldFlatten === true) return true
    }
  }
  return false
}

function emitShouldFlattenIfChanged(): void {
  const shouldFlatten = computeHasAnyShouldFlatten()
  if (shouldFlatten === hasAnyShouldFlatten) return

  hasAnyShouldFlatten = shouldFlatten
  shouldFlattenListeners.forEach(listener => listener())
}
